import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { mx } from "../lib/theme";

/** 2-column catalog tile — image-first grid cell. */
export function MxGridTile({ product, priceLabel, onOpen }) {
  return (
    <ButtonBase onClick={onOpen} sx={{ width: "100%", textAlign: "left", display: "block" }}>
      <Box sx={{ aspectRatio: "1 / 1", bgcolor: mx.inkSoft, overflow: "hidden", mb: 0.85 }}>
        {product.imageUrl ? (
          <Box
            component="img"
            src={product.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
            <Icon sx={{ color: "rgba(255,255,255,0.25)", fontSize: 32 }}>image</Icon>
          </Box>
        )}
      </Box>
      <Typography sx={{ fontFamily: mx.fontMono, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, color: mx.accent, textTransform: "uppercase" }}>
        {product.sku}
      </Typography>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 650,
          lineHeight: 1.25,
          mt: 0.25,
          color: mx.ink,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {product.name}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 750, mt: 0.5, color: mx.ink }}>{priceLabel}</Typography>
    </ButtonBase>
  );
}

MxGridTile.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  priceLabel: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
};

/** Full-width featured row — editorial product strip on home. */
export function MxFeatureRow({ product, priceLabel, onOpen, divider = true }) {
  return (
    <>
      <ButtonBase
        onClick={onOpen}
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "88px 1fr auto",
          gap: 1.25,
          alignItems: "center",
          py: 1.25,
          textAlign: "left",
        }}
      >
        <Box sx={{ width: 88, height: 88, bgcolor: mx.inkSoft, overflow: "hidden", flexShrink: 0 }}>
          {product.imageUrl ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
              <Icon sx={{ color: "rgba(255,255,255,0.25)" }}>image</Icon>
            </Box>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: mx.fontMono, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, color: mx.accent, textTransform: "uppercase" }}>
            {product.sku}
          </Typography>
          <Typography sx={{ fontSize: 16.5, fontWeight: 650, lineHeight: 1.25, mt: 0.2, color: mx.ink }}>{product.name}</Typography>
        </Box>
        <Typography sx={{ fontSize: 15.5, fontWeight: 750, whiteSpace: "nowrap", color: mx.ink }}>{priceLabel}</Typography>
      </ButtonBase>
      {divider ? <Box sx={{ height: "1px", bgcolor: mx.hairline }} /> : null}
    </>
  );
}

MxFeatureRow.propTypes = {
  product: PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    imageUrl: PropTypes.string,
  }).isRequired,
  priceLabel: PropTypes.string.isRequired,
  onOpen: PropTypes.func.isRequired,
  divider: PropTypes.bool,
};

MxFeatureRow.defaultProps = {
  divider: true,
};
